// ContentBlocks.jsx — the FAQ list and the privacy policy, as the shop's own words.
//
// Both were const in the Flutter build until tonight: six FAQ answers in a
// widget tree and a 170-line privacy policy as Dart. Correcting either meant
// a release and a Play review, which in practice means the delivery-charge
// answer stays wrong.
//
// ── WHY ONE SCREEN FOR TWO THINGS ──
// An FAQ is a question, an answer and an order. A policy section is a
// heading, a body and an order. They are the same shape, so they are one
// table and one screen with a selector, rather than two screens that drift
// apart the first time someone improves the reordering on one of them.
//
// Order matters more here than it looks. An FAQ list is read top-down and
// abandoned halfway, so the question people actually ask has to be first;
// a policy read out of order is not a policy. Hence move up / move down on
// every row rather than a sort column.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../../api/axios";
import { PageShell, Button, Toggle, Modal, Pill } from "../component/AdminUi";

/* The kinds this screen edits. `kind` is free text on the server — a shop
   that wants a refund policy should not need a migration — but a selector
   needs a finite list, and these are the two the app asks for. */
const KINDS = [
  {
    key: "faq",
    label: "FAQs",
    blurb: "Shown under Help & Support in the app, in this order.",
    headingLabel: "Question",
    bodyLabel: "Answer",
  },
  {
    key: "privacy",
    label: "Privacy policy",
    blurb:
      "Shown on the Privacy Policy screen. Each row is a numbered section, in this order.",
    headingLabel: "Section heading",
    bodyLabel: "Section text",
  },
];

/* Bullets live inside the body as lines beginning with a bullet character.
   The app splits them back out and renders them as a list, so this is worth
   saying once on screen rather than leaving someone to discover it. */
const BULLET_HINT =
  "Start a line with “• ” to make it a bullet point. Blank lines become paragraphs.";

export default function ContentBlocks() {
  const [kind, setKind] = useState("faq");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, heading, body }
  const [saving, setSaving] = useState(false);

  const current = KINDS.find((k) => k.key === kind) || KINDS[0];

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content-blocks", {
        params: { kind: which },
      });
      setRows(data.data || []);
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not load.") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(kind);
  }, [kind, load]);

  const save = async () => {
    const heading = (editing.heading || "").trim();
    const body = (editing.body || "").trim();
    if (!heading || !body) {
      setNote({ tone: "bad", text: "Both fields are needed." });
      return;
    }
    setSaving(true);
    setNote(null);
    try {
      if (editing.id) {
        await api.put(`/admin/content-blocks/${editing.id}`, { kind, heading, body });
      } else {
        await api.post("/admin/content-blocks", { kind, heading, body });
      }
      setEditing(null);
      await load(kind);
      setNote({ tone: "ok", text: "Saved. The app picks it up on the next open." });
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not save.") });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/content-blocks/${row.id}/status`);
      await load(kind);
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not change that.") });
    }
  };

  /* The whole order goes in one call. Sending one id at a time would leave
     the list half-reordered if the second request failed, and half-reordered
     is worse than not moved. */
  const move = async (index, delta) => {
    const next = [...rows];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setRows(next); // optimistic: the arrows must feel immediate
    try {
      await api.put("/admin/content-blocks/order", { ids: next.map((r) => r.id) });
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not save the order.") });
      await load(kind); // put it back the way the server has it
    }
  };

  return (
    <PageShell
      title="App content"
      count={current.blurb}
      actions={
        <Button
          kind="solid"
          onClick={() => setEditing({ heading: "", body: "" })}
        >
          Add {kind === "faq" ? "question" : "section"}
        </Button>
      }
    >
      {note ? (
        <div className={"ui-banner " + (note.tone === "ok" ? "is-ok" : "is-bad")}>
          {note.text}
        </div>
      ) : null}

      <div className="ui-row-gap" style={{ marginBottom: 18 }}>
        {KINDS.map((k) => (
          <Button
            key={k.key}
            kind={k.key === kind ? "solid" : "quiet"}
            onClick={() => setKind(k.key)}
          >
            {k.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <p className="ui-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="ui-card">
          <p className="ui-muted">
            Nothing here yet. The app shows this section only when it has
            something to show, so it will stay hidden until you add one.
          </p>
        </div>
      ) : (
        rows.map((row, i) => (
          <div className="ui-card" key={row.id}>
            <div className="ui-card__row">
              <div>
                <h3 className="ui-card__sub">
                  {kind === "privacy" ? `${String(i + 1).padStart(2, "0")}. ` : ""}
                  {row.heading}
                </h3>
                {/* A preview, not the whole thing: a policy section runs to a
                    paragraph and the list has to stay scannable. */}
                <p className="ui-muted" style={{ whiteSpace: "pre-line" }}>
                  {row.body.length > 180 ? row.body.slice(0, 180) + "…" : row.body}
                </p>
                {!row.isActive ? <Pill tone="gold">Hidden from the app</Pill> : null}
              </div>

              <div className="ui-row-gap">
                <Button onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
                  ↑
                </Button>
                <Button
                  onClick={() => move(i, 1)}
                  disabled={i === rows.length - 1}
                  title="Move down"
                >
                  ↓
                </Button>
                <Button onClick={() => setEditing({ ...row })}>Edit</Button>
                <Toggle
                  on={!!row.isActive}
                  onChange={() => toggle(row)}
                  label="Show in the app"
                />
              </div>
            </div>
          </div>
        ))
      )}

      <Modal
        open={!!editing}
        title={
          editing?.id
            ? `Edit ${kind === "faq" ? "question" : "section"}`
            : `Add ${kind === "faq" ? "question" : "section"}`
        }
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button kind="solid" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {editing ? (
          <>
            <div className="ui-field">
              <label className="ui-field__label">{current.headingLabel}</label>
              <input
                className="ui-input"
                value={editing.heading}
                onChange={(e) => setEditing({ ...editing, heading: e.target.value })}
                placeholder={
                  kind === "faq" ? "Can I cancel my order?" : "Data We Collect"
                }
              />
            </div>
            <div className="ui-field">
              <label className="ui-field__label">{current.bodyLabel}</label>
              <textarea
                className="ui-input"
                rows={kind === "faq" ? 5 : 12}
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                placeholder="Write it the way you would say it at the counter."
              />
              <p className="ui-field__hint">{BULLET_HINT}</p>
            </div>
          </>
        ) : null}
      </Modal>

      <div className="ui-card">
        <p className="ui-muted">
          Nothing here is deleted — hiding a row keeps it, so a question removed
          by mistake is one switch away from coming back. Changes reach a
          customer the next time they open the screen; no app update is needed.
        </p>
      </div>
    </PageShell>
  );
}

function readError(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}
